import React, { useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';
import { fallbackTestimonials } from '../data';
import ScrollReveal from './ScrollReveal';

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/testimonials');
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            setTestimonials(data);
          } else {
            setTestimonials(fallbackTestimonials);
          }
        } else {
          setTestimonials(fallbackTestimonials);
        }
      } catch (error) {
        console.error('Fejl ved hentning af udtalelser:', error);
        setTestimonials(fallbackTestimonials);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  return (
    <section id="udtalelser" className="py-16 bg-stone-100 border-t border-stone-200/60 relative overflow-hidden">
      {/* Decorative background light */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <ScrollReveal variant="fade-up" duration={800}>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-black text-sm font-semibold tracking-wider uppercase">
              Glade husejere & sommerhusejere
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 mt-2 mb-4 tracking-wider uppercase">
              Kunderne siger om Anlægsgartneren fra Thy
            </h2>
            <p className="text-stone-600 text-lg">
              Min største prioritet er tilfredse kunder, der kan nyde deres uderum hver eneste dag. Her er et udpluk af, hvad lokale husejere siger om mit arbejde.
            </p>
          </div>
        </ScrollReveal>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => {
            const textHtml = typeof testimonial.text === 'object' ? testimonial.text.da : testimonial.text;
            const rating = testimonial.rating || 5;

            return (
              <ScrollReveal
                key={testimonial.id || index}
                variant="fade-up"
                duration={800}
                delay={index * 150}
                className="h-full"
              >
                <div
                  className="relative bg-white border border-stone-200/80 rounded-2xl p-8 flex flex-col justify-between shadow-sm h-full"
                >
                  <div>
                    {/* Star Rating */}
                    <div className="flex items-center gap-1 mb-5">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-5 w-5 ${
                            i < rating ? 'text-emerald-500 fill-emerald-500' : 'text-stone-200'
                          }`}
                        />
                      ))}
                    </div>

                    {/* Testimonial Text */}
                    <div
                      className="text-stone-600 text-sm sm:text-base leading-relaxed mb-6 italic prose prose-stone"
                      dangerouslySetInnerHTML={{ __html: textHtml }}
                    />
                  </div>

                  {/* Customer Info */}
                  <div className="pt-6 border-t border-stone-100 flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-stone-900 text-sm uppercase tracking-wider">
                        {testimonial.name}
                      </h4>
                      <p className="text-stone-900 text-xs font-medium mt-0.5">
                        Husejer i {testimonial.location}
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-stone-500 bg-stone-100 px-2.5 py-1 rounded-md border border-stone-200">
                      Verificeret kunde
                    </span>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        {/* Trustpilot-style Badge */}
        <ScrollReveal variant="fade-in" duration={800} delay={300}>
          <div className="mt-12 text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-3">
              <span className="text-stone-600 text-sm font-medium">Fremragende kvalitet og service</span>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-emerald-500 fill-emerald-500" />
                ))}
              </div>
              <span className="text-stone-900 text-sm font-bold">5.0 ud af 5 stjerner</span>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
